import type { Locale } from "@adpilot/shared";
import { useQuery } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { marketplaceCode } from "@/components/layout/shop-scope-select";
import { fetchAmazonAccounts } from "@/lib/amazon-api";
import {
  addReportingDays,
  type ReportingTimeZone,
  reportingDateInTimeZone,
} from "@/lib/reporting-time-zone";

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

/** Compact remaining time for review deadlines: "45m left", "3h left", "2d left", "Expired". */
export function formatTimeLeft(
  expiresAt: string,
  now: Date,
  t: TFunction<"dashboard">,
): { label: string; urgent: boolean } {
  const remaining = new Date(expiresAt).getTime() - now.getTime();
  if (remaining <= 0) return { label: t("overview.aiStatus.timeLeft.expired"), urgent: true };
  if (remaining < HOUR_MS) {
    return {
      label: t("overview.aiStatus.timeLeft.minutes", {
        count: Math.max(1, Math.floor(remaining / MINUTE_MS)),
      }),
      urgent: true,
    };
  }
  if (remaining < 48 * HOUR_MS) {
    return {
      label: t("overview.aiStatus.timeLeft.hours", { count: Math.floor(remaining / HOUR_MS) }),
      urgent: remaining < 6 * HOUR_MS,
    };
  }
  return {
    label: t("overview.aiStatus.timeLeft.days", { count: Math.floor(remaining / (24 * HOUR_MS)) }),
    urgent: false,
  };
}

/** "Today 02:10" / "Tomorrow 02:10" / "Sep 9 02:10", in the reporting time zone. */
export function formatRunMoment(
  iso: string,
  now: Date,
  timeZone: ReportingTimeZone,
  locale: Locale,
  t: TFunction<"dashboard">,
): string {
  const date = new Date(iso);
  const time = new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
  const day = reportingDateInTimeZone(date, timeZone);
  const today = reportingDateInTimeZone(now, timeZone);
  const relative =
    day === today
      ? t("overview.aiStatus.day.today")
      : day === addReportingDays(today, 1)
        ? t("overview.aiStatus.day.tomorrow")
        : day === addReportingDays(today, -1)
          ? t("overview.aiStatus.day.yesterday")
          : new Intl.DateTimeFormat(locale, { timeZone, month: "short", day: "numeric" }).format(
              date,
            );
  return `${relative} ${time}`;
}

/** Whole-unit money for dense tables ("$9,840"); currency follows the summary's reporting currency. */
export function wholeCurrencyOptions(
  currency: string | null | undefined,
): Intl.NumberFormatOptions {
  return {
    style: "currency",
    currency: currency ?? "USD",
    currencyDisplay: (currency ?? "USD") === "USD" ? "narrowSymbol" : "symbol",
    maximumFractionDigits: 0,
  };
}

/**
 * Shop → two-letter marketplace code, shared with the shop-scope selector's
 * cached accounts query so the overview adds no extra request.
 */
export function useShopMarketplaceCodes(): ReadonlyMap<string, string> {
  const { data } = useQuery({
    queryKey: ["amazon", "accounts"],
    queryFn: fetchAmazonAccounts,
    staleTime: 60_000,
  });
  return new Map(
    data?.connections.flatMap((connection) =>
      connection.shops.map((shop) => [shop.shopId, marketplaceCode(shop.marketplaceName)] as const),
    ) ?? [],
  );
}

/** Inclusive day count of a resolved reporting period ("2026-09-01".."2026-09-30" → 30). */
export function periodDayCount(startDate: string, endDate: string): number {
  const days = Math.round(
    (Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / 86_400_000,
  );
  return Math.max(1, days + 1);
}
